import { StrokedText } from "@/components/ui/text/stroked-text";
import { TeamCard } from "./team-card";
import { useEffect, useState, useCallback } from "react";
import { RouteNames } from "@/types/route-names";
import { useSPAContext } from "@/providers/spa-provider";
import { useBackAction } from "@/hooks/use-back-action";
import { Button } from "@/components/ui/buttons/button";
import { listPokeTeams } from "@/services/poketeam/list-poketeams";
import { updatePokeTeam } from "@/services/poketeam/update-poketeam";
import { useAuth } from "@/providers/auth-provider";
import { useUser } from "@/providers/user-provider";
import { Alert } from "@/components/alert/alert";
import { ListTeamsOutput } from "types/web-app";
import { createPokeTeam } from "@/services/poketeam/create-poketeam";
import { Dialog } from "@/components/ui/dialog/dialog";
import { StandardFrame } from "@/components/ui/frames/standard-frame";
import { PokeTeam } from "@/types";

/**
 * Team List View - Displays the player's team list
 */
export const TeamListView = () => {
  const { setCurrentRoute, setSelectedTeam, previousRoute } = useSPAContext();
  const { getAccessToken } = useAuth();
  const { reloadProfile } = useUser();

  const [mainTeamLoadingId, setMainTeamLoadingId] = useState<number | null>(
    null,
  );

  const handleBack = useCallback(() => {
    setCurrentRoute(previousRoute);
  }, [previousRoute, setCurrentRoute]);

  useBackAction(handleBack);

  const [teams, setTeams] = useState<PokeTeam[]>();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [newTeamName, setNewTeamName] = useState("");
  const [isCreatingTeam, setIsCreatingTeam] = useState(false);

  const handleTeamClick = useCallback(
    (team: PokeTeam) => {
      setSelectedTeam(team);
      setCurrentRoute(RouteNames.TEAM_EDIT);
    },
    [setCurrentRoute, setSelectedTeam],
  );

  const getTeams = useCallback(async () => {
    const accessToken = getAccessToken();
    if (!accessToken) {
      Alert("Authentication error. Please try again");
      return;
    }

    const response = await listPokeTeams(accessToken);

    const teams = response.data.teams;
    setTeams(teams);
  }, [getAccessToken]);

  const createTeam = useCallback(async () => {
    if (isCreatingTeam) return;

    if (!newTeamName.trim()) {
      Alert("Team name cannot be empty");
      return;
    }

    setIsCreatingTeam(true);
    const accessToken = getAccessToken();
    if (!accessToken) {
      Alert("Authentication error. Please try again");
      setIsCreatingTeam(false);
      return;
    }

    try {
      const response = await createPokeTeam(accessToken, {
        name: newTeamName.trim(),
      });

      if (response.ok && response.data?.team) {
        // Set the newly created team as selected and navigate to edit
        setSelectedTeam({
          id: response.data.team.id,
          name: response.data.team.name,
          order: null,
          pokemons: [],
          isMainTeam: false,
        });
        setCurrentRoute(RouteNames.TEAM_EDIT);
        setIsCreateDialogOpen(false);
        setNewTeamName("");
      } else {
        Alert(response.message || "Failed to create team. Please try again.");
      }
    } catch (error) {
      console.error("Failed to create team:", error);
      Alert("Failed to create team. Please try again.");
    } finally {
      setIsCreatingTeam(false);
    }
  }, [
    getAccessToken,
    isCreatingTeam,
    newTeamName,
    setCurrentRoute,
    setSelectedTeam,
  ]);

  const handleSetMainTeam = useCallback(
    async (teamId: number) => {
      const accessToken = getAccessToken();
      if (!accessToken) {
        Alert("Authentication error. Please try again");
        return;
      }
      setMainTeamLoadingId(teamId);
      try {
        const result = await updatePokeTeam(accessToken, {
          team_id: teamId,
          set_main_team: true,
        });
        if (result.ok) {
          await Promise.all([getTeams(), reloadProfile()]);
        } else {
          Alert(result.message || "Failed to set main team.");
        }
      } catch {
        Alert("Failed to set main team. Please try again.");
      } finally {
        setMainTeamLoadingId(null);
      }
    },
    [getAccessToken, getTeams, reloadProfile],
  );

  useEffect(() => {
    void getTeams();
  }, [getTeams]);

  return (
    <div className="relative overflow-visible w-full min-h-screen bg-pk-black/80 backdrop-blur-[3px]">
      <div className="flex flex-col w-full relative pb-12 overflow-visible">
        {/* Header */}
        <div className="relative flex items-center justify-between w-full px-4 mt-4">
          <div className="absolute hidden xs:block h-1/2 w-full left-0 top-1/2 -translate-y-1/2 bg-pk-gray-1"></div>
          <StrokedText strokeWidth={12} fontSize={24} className="z-10">
            MY TEAMS
          </StrokedText>

          <Button
            variant="blue"
            className="z-10"
            onClick={() => setIsCreateDialogOpen(true)}
          >
            New team
          </Button>
        </div>

        <div className="flex flex-col px-2 gap-3 mt-3 xs:mt-2">
          {/* Teams - Scrollable */}
          {!teams && (
            <StandardFrame variant="black" className="w-full p-4">
              <div className="flex flex-col items-center justify-center py-8 animate-pulse">
                <StrokedText strokeWidth={10} fontSize={20} className="z-10">
                  Loading teams...
                </StrokedText>
                <p className="text-pk-gray-4 text-xs mt-2">
                  This should only take a moment
                </p>
              </div>
            </StandardFrame>
          )}

          {teams && teams.length > 0 && (
            <TeamList
              teams={teams}
              handleTeamClick={handleTeamClick}
              onSetMainTeam={handleSetMainTeam}
              mainTeamLoadingId={mainTeamLoadingId}
            />
          )}

          {teams && teams.length === 0 && (
            <StandardFrame variant="blue" className="w-full p-4">
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <StrokedText strokeWidth={10} fontSize={20} className="z-10">
                  No teams yet
                </StrokedText>
                <p className="text-pk-gray-4 text-sm mt-2 max-w-sm">
                  Create your first team to start building your lineup.
                </p>
                <Button
                  variant="blue"
                  className="mt-4"
                  onClick={() => setIsCreateDialogOpen(true)}
                >
                  Create team
                </Button>
              </div>
            </StandardFrame>
          )}
        </div>

        {/* Create Team Dialog */}
        <CreateTeamDialog
          isOpen={isCreateDialogOpen}
          onClose={() => {
            setIsCreateDialogOpen(false);
            setNewTeamName("");
          }}
          teamName={newTeamName}
          onTeamNameChange={setNewTeamName}
          onCreate={createTeam}
          isCreating={isCreatingTeam}
        />
      </div>
    </div>
  );
};

const TeamList = ({
  teams,
  handleTeamClick,
  onSetMainTeam,
  mainTeamLoadingId,
}: {
  teams: PokeTeam[];
  handleTeamClick: (team: PokeTeam) => void;
  onSetMainTeam: (teamId: number) => void;
  mainTeamLoadingId: number | null;
}) => {
  return (
    <div className="max-h-[85vh] flex flex-col gap-3 grow overflow-y-auto px-1 xs:px-2 pb-2">
      {teams?.map((team) => (
        <TeamCard
          key={team.id}
          team={team}
          onClick={handleTeamClick}
          onSetMainTeam={onSetMainTeam}
          mainTeamLoadingId={mainTeamLoadingId}
        />
      ))}
    </div>
  );
};

const CreateTeamDialog = ({
  isOpen,
  onClose,
  teamName,
  onTeamNameChange,
  onCreate,
  isCreating,
}: {
  isOpen: boolean;
  onClose: () => void;
  teamName: string;
  onTeamNameChange: (name: string) => void;
  onCreate: () => void;
  isCreating: boolean;
}) => {
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => setIsAnimating(true));
    } else {
      setIsAnimating(false);
    }
  }, [isOpen]);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !isCreating && teamName.trim()) {
      onCreate();
    } else if (e.key === "Escape") {
      onClose();
    }
  };

  return (
    <Dialog isOpen={isOpen} onClose={onClose}>
      <div
        className={`w-[85vw] max-w-md mx-auto transition-all duration-200 ease-out transform ${
          isAnimating ? "scale-100 opacity-100" : "scale-95 opacity-0"
        }`}
      >
        <StandardFrame variant="blue" className="w-full p-4">
          <div className="text-center">
            <StrokedText strokeWidth={10} fontSize={18} className="z-10">
              CREATE TEAM
            </StrokedText>
            <p className="text-pk-gray-4 mt-2 mb-6 text-sm">
              Give your team a name to get started.
            </p>

            <div className="mb-6">
              <div className="flex flex-row items-center gap-2 bg-pk-blue-3/50 rounded px-3 py-2 border-2 border-pk-blue-4">
                <input
                  type="text"
                  value={teamName}
                  onChange={(e) => onTeamNameChange(e.target.value)}
                  onKeyDown={handleKeyPress}
                  placeholder="Team name..."
                  className="w-full text-lg bg-transparent outline-none border-none text-pk-white placeholder-pk-gray-4 text-center font-semibold"
                  maxLength={20}
                  disabled={isCreating}
                  autoFocus
                />
              </div>
              <p className="text-xs text-pk-gray-4 mt-2">
                {teamName.length}/20
              </p>
            </div>

            <div className="flex gap-4 items-center justify-center w-full">
              <Button
                variant="gray"
                onClick={onClose}
                disabled={isCreating}
                className="flex-1"
              >
                CANCEL
              </Button>
              <Button
                variant="blue"
                onClick={onCreate}
                disabled={isCreating || !teamName.trim()}
                className="flex-1"
              >
                {isCreating ? "CREATING..." : "CREATE TEAM"}
              </Button>
            </div>
          </div>
        </StandardFrame>
      </div>
    </Dialog>
  );
};
